import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { ApiError, sanitizeApiErrorMessage } from "@/shared/lib/apiClient";
import { fetchAdminPackage, type AdminPackageDetail } from "../api/adminApi";
import { getAuthToken } from "../auth";

function formatMoney(value: number) {
  return `${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
}

function formatDate(value: string | null | undefined) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("fr-FR");
}

export function PackageDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const packageId = Number(id);
  const labels = t.safecheck.packages;
  const [pkg, setPkg] = useState<AdminPackageDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!getAuthToken() || !packageId) {
      setError("Package introuvable.");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchAdminPackage(packageId)
      .then((row) => {
        if (!cancelled) setPkg(row);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          setError(null);
          return;
        }
        setError(
          sanitizeApiErrorMessage(
            err instanceof Error ? err.message : "Impossible de charger le package.",
          ),
        );
        setPkg(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [packageId]);

  const includedActs = useMemo(
    () => (pkg?.acts ?? []).filter((act) => act.is_included),
    [pkg],
  );
  const excludedActs = useMemo(
    () => (pkg?.acts ?? []).filter((act) => !act.is_included),
    [pkg],
  );

  const statusVariant =
    pkg?.status === "active"
      ? "success"
      : pkg?.status === "draft"
        ? "warning"
        : pkg?.status === "suspended"
          ? "danger"
          : "neutral";

  if (loading) {
    return <div className="theme-card p-8 text-center text-sm text-muted">Chargement…</div>;
  }

  if (!pkg) {
    return (
      <div className="theme-card p-8 text-center">
        <p className="text-sm text-muted">{error ?? labels.empty}</p>
        <Link to="/dashboard/packages" className="mt-4 inline-flex items-center gap-2 text-sm text-primary">
          <ArrowLeft size={14} />
          {labels.create.backToList}
        </Link>
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs
        items={[
          { label: labels.title, href: "/dashboard/packages" },
          { label: pkg.code || `Package #${pkg.id}` },
        ]}
      />

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">{pkg.name}</h1>
          <p className="mt-1 text-sm text-muted">
            {pkg.code} · {pkg.hospital} · {pkg.company}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge variant={statusVariant}>{pkg.status}</StatusBadge>
          <Link
            to={`/dashboard/packages/${pkg.id}/edit`}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            <Pencil size={14} />
            {labels.create.editTitle}
          </Link>
          <Link
            to="/dashboard/packages"
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-card-muted"
          >
            <ArrowLeft size={14} />
            {labels.create.backToList}
          </Link>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <div className="theme-card p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">Période</p>
          <p className="mt-1 text-sm font-semibold text-foreground">
            {pkg.period_start} → {pkg.period_end}
          </p>
        </div>
        <div className="theme-card p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">Actes</p>
          <p className="mt-1 text-sm font-semibold text-foreground">
            {pkg.included_acts} inclus · {pkg.excluded_acts} exclus
          </p>
        </div>
        <div className="theme-card p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">Créé le</p>
          <p className="mt-1 text-sm font-semibold text-foreground">{formatDate(pkg.created_at)}</p>
        </div>
      </div>

      {pkg.description ? (
        <div className="theme-card mb-5 p-5">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Description</h2>
          <p className="text-sm text-foreground whitespace-pre-wrap">{pkg.description}</p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <section className="theme-card overflow-hidden">
          <div className="border-b border-border px-5 py-3">
            <h2 className="text-sm font-semibold text-foreground">
              {labels.included} ({includedActs.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px]">
              <thead>
                <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                  <th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2">Libellé</th>
                  <th className="px-4 py-2 text-right">Coût</th>
                  <th className="px-4 py-2 text-right">Limite</th>
                </tr>
              </thead>
              <tbody>
                {includedActs.map((act) => (
                  <tr key={act.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 text-sm font-mono text-foreground">{act.code}</td>
                    <td className="px-4 py-2 text-sm text-foreground">{act.label}</td>
                    <td className="px-4 py-2 text-right text-sm text-muted">
                      {act.cost != null ? formatMoney(act.cost) : "—"}
                    </td>
                    <td className="px-4 py-2 text-right text-sm text-muted">
                      {act.quantity_limit != null
                        ? `${act.quantity_limit}${act.time_period ? ` / ${act.time_period}` : ""}`
                        : "—"}
                    </td>
                  </tr>
                ))}
                {includedActs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted">
                      Aucun acte inclus
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section className="theme-card overflow-hidden">
          <div className="border-b border-border px-5 py-3">
            <h2 className="text-sm font-semibold text-foreground">
              {labels.excluded} ({excludedActs.length})
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[420px]">
              <thead>
                <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
                  <th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2">Libellé</th>
                  <th className="px-4 py-2 text-right">Coût</th>
                </tr>
              </thead>
              <tbody>
                {excludedActs.slice(0, 50).map((act) => (
                  <tr key={act.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 text-sm font-mono text-foreground">{act.code}</td>
                    <td className="px-4 py-2 text-sm text-foreground">{act.label}</td>
                    <td className="px-4 py-2 text-right text-sm text-muted">
                      {act.cost != null ? formatMoney(act.cost) : "—"}
                    </td>
                  </tr>
                ))}
                {excludedActs.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-sm text-muted">
                      Aucun acte exclus
                    </td>
                  </tr>
                ) : null}
                {excludedActs.length > 50 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-3 text-center text-xs text-muted">
                      … et {excludedActs.length - 50} autres
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
}
